import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Lock, MessageCircle, LogOut, Clock, ShieldAlert, RefreshCw } from "lucide-react";

/**
 * Tela neutra exibida quando a licença da empresa ativa está bloqueada.
 * - reason="disabled": administrador desativou manualmente
 * - reason="expired":  data de expiração passou
 *
 * Props:
 *   status: { reason, empresa_nome, expires_at, observacao, support: { whatsapp_suporte, mensagem_padrao } }
 *   variant: "web" | "mobile"
 */
export default function LicenseBlockedScreen({ status, variant = "web" }) {
  const { logout, user } = useAuth();
  const [supportOverride, setSupportOverride] = useState(null);

  // Se o backend não incluiu support (ex: cache antigo), busca separadamente.
  useEffect(() => {
    if (status?.support) return;
    api.get("/license/support-info").then((r) => setSupportOverride(r.data)).catch(() => {
      /* silencioso — botão de WhatsApp fica desabilitado */
    });
  }, [status?.support]);

  const support = status?.support || supportOverride || {};
  const reason = status?.reason || "disabled";
  const empresaNome = status?.empresa_nome || "sua empresa";
  const expiresAt = status?.expires_at;
  const observacao = status?.observacao;
  const whatsappRaw = support?.whatsapp_suporte || "";
  const whatsappDigits = whatsappRaw.replace(/\D/g, "");
  const canWhatsapp = whatsappDigits.length >= 10;

  const mensagemPadrao = support?.mensagem_padrao
    || `Olá! Sou usuário da empresa *${empresaNome}* e o acesso ao sistema está bloqueado por licenciamento. Poderia me ajudar a regularizar?`;

  const wppHref = canWhatsapp
    ? `https://wa.me/${whatsappDigits}?text=${encodeURIComponent(mensagemPadrao)}`
    : "#";

  const title = reason === "expired" ? "Licença Expirada" : "Licença Inativa";
  const headline = reason === "expired"
    ? `Sua licença expirou${expiresAt ? ` em ${fmtDate(expiresAt)}` : ""}.`
    : "Sua licença está inativa no momento.";

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-slate-50 via-orange-50/40 to-slate-100 p-4"
      data-testid={`license-blocked-screen-${variant}`}
    >
      <div className="max-w-md w-full bg-white border border-orange-200 rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-orange-600 to-orange-500 p-6 text-white text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mb-3">
            {reason === "expired"
              ? <Clock className="w-9 h-9" data-testid="license-icon-expired" />
              : <Lock className="w-9 h-9" data-testid="license-icon-disabled" />}
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight">{title}</h1>
          <p className="text-sm opacity-90 mt-1">Regularize para voltar a acessar o sistema</p>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-lg p-3">
            <ShieldAlert className="w-5 h-5 text-orange-700 shrink-0 mt-0.5" />
            <div className="text-sm text-orange-900">
              <p className="font-semibold" data-testid="license-empresa-nome">{empresaNome}</p>
              <p className="mt-0.5">{headline}</p>
            </div>
          </div>

          {observacao && (
            <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900" data-testid="license-observacao">
              {observacao}
            </div>
          )}

          <p className="text-sm text-slate-600 leading-relaxed">
            Enquanto a licença estiver bloqueada, nenhum usuário desta empresa poderá acessar
            o Dashboard, App Mobile, cadastros ou relatórios. Para regularizar, entre em
            contato com o suporte.
          </p>

          <div className="space-y-2 pt-2">
            <Button
              asChild={canWhatsapp}
              disabled={!canWhatsapp}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              data-testid="license-btn-whatsapp"
            >
              {canWhatsapp ? (
                <a href={wppHref} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Falar com o Suporte
                </a>
              ) : (
                <span>
                  <MessageCircle className="w-5 h-5 mr-2 inline" />
                  Suporte indisponível
                </span>
              )}
            </Button>

            {!canWhatsapp && (
              <p className="text-[11px] text-center text-muted-foreground" data-testid="license-no-whatsapp-msg">
                Contato de suporte ainda não configurado — peça ao administrador do sistema.
              </p>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => window.location.reload()}
                data-testid="license-btn-reload"
              >
                <RefreshCw className="w-4 h-4 mr-2" />Tentar de novo
              </Button>
              <Button
                variant="outline"
                className="flex-1 text-rose-600 border-rose-200 hover:bg-rose-50"
                onClick={() => logout()}
                data-testid="license-btn-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />Sair
              </Button>
            </div>
          </div>

          {user?.email && (
            <p className="text-[11px] text-center text-muted-foreground pt-2 border-t">
              Sessão: <span className="font-mono">{user.email}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
